import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { TableVirtualScrollDataSource } from 'ng-table-virtual-scroll';
import { MatMenu } from '@angular/material/menu';
import { PermissionsService } from '../../services/permissions.service';
import { NavState } from '../../types/common';
import { DropdownOption } from '../../types/forms';

type ListItem<T> = {
  selected: boolean;
  data: T;
};

export type DynamicListColumnDef = {
  key: string;
  title: string;
  template?: TemplateRef<any>;
};

export type DynamicListItemAction<T> = {
  label: string;
  icon?: string;
  callback: (item: T) => void;
  disabledPredicate?: (item: T) => boolean;
};

export type DynamicListSelectionAction<T> = {
  label: string;
  icon?: string;
  callback: (items: T[]) => void;
};

export enum DynamicListFilterType {
  TEXT,
  DROPDOWN,
}

export type DynamicListFilterValue = {
  key?: string;
  value?: string;
};

export type DynamicListFilter<T> = {
  key?: string;
  type: DynamicListFilterType;
  label: string;
  anyLabel?: string;
  options?: DropdownOption<T>[];
  multiple?: boolean;
  value?: any;
};

type DynamicListFilterInner<T> = DynamicListFilter<T> & {
  formKey: string;
};

export type DynamicListFilterPredicate<T> = (data: T, filter: Object) => boolean;

export interface DynamicListNavState extends NavState {
  searchText?: string;
  filters?: DynamicListFilterValue[];
}

const searchInObject = (obj, searchTerm) => {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (typeof obj[key] === 'object') {
        if (searchInObject(obj[key], searchTerm)) {
          return true;
        }
      } else if (`${obj[key]}`.includes(searchTerm)) {
        return true;
      } else {
      }
    }
  }

  return false;
};

const STATIC_COLUMNS_KEYS = {
  SELECTION: 'checkboxes',
  ACTIONS: 'actions',
};

const NO_KEY_FILTER = 'noKeyFilter::';

const defaultPredicate: DynamicListFilterPredicate<any> = (data: any, filters) => {
  if (!filters) {
    return true;
  }

  return Object.keys(filters).every((filterKey) => {
    const filterValue = filters[filterKey];

    if (filterValue === null || filterValue === undefined) {
      return true;
    }

    if (filterKey.includes(NO_KEY_FILTER)) {
      return searchInObject(data, filterValue);
    }

    const keyWords = filterKey.split('.');
    const value = keyWords.reduce((prev, curr) => prev && prev[curr], data);

    if (typeof filterValue === 'number' || typeof filterValue === 'boolean') {
      return value === filterValue;
    } else if (typeof filterValue === 'string') {
      return `${value}`.includes(filterValue);
    }
  });
};

@Component({
  selector: 'cm-dynamic-list',
  templateUrl: './dynamic-list.component.html',
  styleUrls: ['./dynamic-list.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicListComponent<T, L> implements OnChanges {
  DynamicListFilterType = DynamicListFilterType;

  @Input() data: Array<T>;
  @Input() columns: DynamicListColumnDef[];
  @Input() actions: DynamicListItemAction<T>[] = [];
  @Input() selectionActions: DynamicListSelectionAction<T>[];
  @Input() filters: DynamicListFilter<L>[];
  @Input() filterPredicate: DynamicListFilterPredicate<T> = defaultPredicate;
  @Input() rowClassGetter: (item: T) => string;

  @Input() emptyPlaceholder = 'Não existem items a mostrar';
  @Input() addNeededScopes: string[] = [];
  @Input() addLabel: string = 'Adicionar';
  @Input() canAdd: boolean = true;
  @Input() isLoading: boolean;

  @Output() itemClick = new EventEmitter<T>();
  @Output() add = new EventEmitter<void>();
  @Output() filterChange = new EventEmitter<DynamicListFilterValue[]>();

  @ViewChild('virtualScroll', { read: CdkVirtualScrollViewport })
  virtualScroll: CdkVirtualScrollViewport;

  JSON  = JSON;

  @ViewChildren('selectedItemsMenu', { read: MatMenu })
  selectedItemsMenuTriggers: MatMenu[];

  displayedColumnKeys: string[] = [];
  dataSource = new TableVirtualScrollDataSource([]);

  allControl = new FormControl(false);
  filtersControlsGroup = new UntypedFormGroup({});

  allFilters: DynamicListFilterInner<L>[] = [];

  hasFilters = false;

  get itemClickHasListeners() {
    return this.itemClick.observers.length > 0;
  }

  get isDataLoading() {
    return this.isLoading ?? this.data === undefined;
  }

  get selectedOptionsCount() {
    return this.dataSource ? this.dataSource.filteredData.filter((item) => item.selected).length : 0;
  }

  get canAddItem() {
    return this.canAdd && this.permissionsService.userHasAllScopes(this.addNeededScopes);
  }

  get isEmpty() {
    return !this.dataSource.data.length;
  }

  get isFilteredEmpty() {
    return !this.dataSource.filteredData.length;
  }

  get placeholder() {
    return this.isEmpty ? this.emptyPlaceholder : `${this.emptyPlaceholder} (com filtros aplicados)`
  }

  constructor(private changeDetectorRef: ChangeDetectorRef, private permissionsService: PermissionsService) {
    this.allControl.valueChanges.subscribe((value) => {
      value ? this.setSelectionToAll(true) : this.setSelectionToAll(false);
    });

    this.filtersControlsGroup.valueChanges.subscribe(() => {
      this.dataSource.filter = this.filtersControlsGroup.value;
      this.filterChange.emit(Object.keys(this.filtersControlsGroup.value).map((key) => ({
        key,
        value: this.filtersControlsGroup.value[key],
      })));

      this.checkIfHasFilters();
    });
  }

  selectionPanelOpened(expanded) {
    expanded ? this.showCheckboxes() : this.hideCheckboxes();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data?.currentValue) {
      this.buildDataSource(changes.data.currentValue as T[]);
    }

    if (changes.actions?.currentValue || changes.columns?.currentValue) {
      this.buildColumns();
    }

    if (changes.filters?.currentValue) {
      this.removeAllFilters();

      this.allFilters = (changes.filters.currentValue as DynamicListFilter<L>[]).map((filter) => ({
        ...filter,
        anyLabel: filter.anyLabel || `${filter.label} indiferente`,
        formKey: filter.key || `${NO_KEY_FILTER}${Math.round(Math.random() * 1000)}`,
      }));

      this.allFilters.forEach((filter) => {
        const newFilterControl = new FormControl();

        if (filter.type === DynamicListFilterType.DROPDOWN) {
          const selectedOption = filter.options?.filter((option) => option.selected);

          if (filter.multiple) {
            newFilterControl.setValue(selectedOption?.map((option) => option.value))
          } else {
            newFilterControl.setValue(selectedOption?.[0]?.value);
          }

          filter.options = [...(filter.multiple ? [] : [{ text: filter.anyLabel, value: null }]), ...filter.options];
        }

        if (filter.type === DynamicListFilterType.TEXT) {
          newFilterControl.setValue(filter.value);
        }

        this.filtersControlsGroup.addControl(filter.formKey, newFilterControl);
      });

      this.changeDetectorRef.detectChanges();
    }
  }

  addCallback() {
    this.add.emit();
  }

  clearFilter(formKey: string) {
    this.filtersControlsGroup.get(formKey).setValue(null);
  }

  getFilterControl(key: string) {
    return this.filtersControlsGroup.controls[key];
  }

  getColumnValue(item: ListItem<T>, columnKey: string) {
    return columnKey.split('.').reduce((prev, curr) => prev && prev[curr], item.data);
  }

  itemSelectionToggled() {
    this.changeDetectorRef.detectChanges();
  }

  selectionActionClicked(action: DynamicListSelectionAction<T>) {
    const selected = this.dataSource.data.filter((item) => item.selected).map((listItem) => listItem.data);

    action.callback(selected);
  }

  openContextMenu(event: MouseEvent, item: ListItem<T>, menu: MatMenu) {
    event.stopPropagation();
  }

  actionDisabled(action: DynamicListItemAction<T>, item: ListItem<T>) {
    return action.disabledPredicate?.(item.data);
  }

  actionClicked(clickEvent: MouseEvent, action: DynamicListItemAction<T>, item: ListItem<T>) {
    // clickEvent.stopPropagation();
    action.callback(item.data);
  }

  rowClicked(event: MouseEvent, item: ListItem<T>) {
    this.itemClick.emit(item.data);
  }

  getRowClass(item: ListItem<T>) {
    return [this.rowClassGetter ? this.rowClassGetter(item.data) : '', this.itemClickHasListeners ? 'clickable' : ''].join(' ');
  }

  clickedCheckboxBorder(event: MouseEvent, item: ListItem<T>) {
    item.selected = !item.selected;
    event.stopPropagation();
    this.changeDetectorRef.detectChanges();
  }

  private buildDataSource(items?: T[]) {
    this.dataSource = new TableVirtualScrollDataSource(items.map((item) => ({ selected: false, data: item } as ListItem<T>)));
    this.dataSource.filterPredicate = (item: ListItem<T>, filter: string) => this.filterPredicate(item.data, filter);
    this.dataSource.filter = this.filtersControlsGroup.value;

    this.changeDetectorRef.detectChanges();
  }

  private buildColumns({ showActions = true, showSelection = false } = {}) {
    const columns = (this.columns as DynamicListColumnDef[]) || [];

    this.displayedColumnKeys = columns.map((col) => col.key);

    if (showSelection) {
      this.displayedColumnKeys = [STATIC_COLUMNS_KEYS.SELECTION, ...this.displayedColumnKeys];
    }

    if (showActions && this.actions?.length) {
      this.displayedColumnKeys = [...this.displayedColumnKeys, 'actions'];
    }

    this.changeDetectorRef.detectChanges();
  }

  private setSelectionToAll(selected: boolean) {
    this.dataSource.filteredData.forEach((item) => (item.selected = selected));
  }

  private showCheckboxes() {
    this.buildColumns({ showSelection: true });
  }

  private hideCheckboxes() {
    this.buildColumns({ showSelection: false });
  }

  private removeAllFilters() {
    Object.keys(this.filtersControlsGroup.controls).forEach((key) => this.filtersControlsGroup.removeControl(key, { emitEvent: false }));

    this.allFilters = [];
  }

  private checkIfHasFilters() {
    const filterValue = this.filtersControlsGroup.value;
    const filterKeys = Object.keys(filterValue);

    this.hasFilters = filterKeys.some((key) => {
      if (Array.isArray(filterValue[key])) {
        return filterValue[key]?.length > 0;
      }

      if (typeof filterValue[key] === 'string') {
        return filterValue[key]?.trim().length > 0;
      }

      return filterValue[key] !== null && filterValue[key] !== undefined;
    });

    this.changeDetectorRef.detectChanges();
  }
}
